/**
 * The shared Plan Overview's grid — a read-only, simpler twin of the working plan grid
 * (PlanGrid.tsx): Plan Lines by month, grouped by Workstream, the frozen block on the
 * left, the Total / Revenue / FTE rows pinned to the bottom.
 *
 * It INHERITS the working grid's look rather than copying it. The header and footer cells,
 * the month column width, the formatters, the Workstream type chip and the Open / Proposed
 * / Confirmed chip all come from PlanGrid.tsx, and the cells written here use the same
 * tokens, so a restyle of the working grid carries over to the view a plan is shared as.
 *
 * Simpler on purpose. Every figure is text: no inputs, no selection, fill, paste or undo,
 * no ramp row, no add rows, no menus. And it draws only what the shared payload carries,
 * which is bill-side only (src/lib/plan-share-view.ts) — so the working grid's Who column
 * (its initials come from a name), load bars (a Resource's guidance), negotiated-rate
 * highlight (rate lineage) and estimate chips have nothing to draw from, and are absent.
 *
 * Hours are EFFECTIVE: what each month bills once any ramp is applied. Every row adds up
 * to its Hours figure and every column to its Total, and both agree with the Delivery
 * Shape table on the same page. On a ramped plan that is not what was typed into the
 * working grid's cells, and the note under the grid says so.
 */

import { useLayoutEffect, useMemo, useState } from "react";
import {
  FOOT_SUB_ROW_H,
  FootCell,
  fmtMoney,
  fmtNum,
  fmtWholeMoney,
  HeadCell,
  LineStatusBadge,
  monthLabel,
  type Slot,
  W_MONTH,
  WorkstreamTypeChip,
} from "@/components/plans/PlanGrid";
import { type SharedGridGroup, type SharedGridLine, sharedGridGroups } from "@/lib/plan-share-grid";
import { cn } from "@/lib/utils";
import type { SharedPlanView } from "@/schemas/resource-plans";

interface Geo {
  category: Slot;
  rate: Slot;
  hours: Slot;
  amount: Slot;
  frozenWidth: number;
  frozenCols: number;
}

/**
 * The working grid's frozen block (`buildGeo` in PlanGrid.tsx), less its Who and run-rate
 * columns. The Who column's 72px goes to the name instead, so the block is as wide as the
 * working grid's and a long Labor Category truncates later.
 */
function buildGeo(): Geo {
  let cursor = 0;
  const col = (width: number): Slot => {
    const left = cursor;
    cursor += width;
    return { left, width };
  };
  const category = col(320);
  const rate = col(72);
  const hours = col(68);
  const amount = col(92);
  return { category, rate, hours, amount, frozenWidth: cursor, frozenCols: 4 };
}

const GEO = buildGeo();

/** The frozen columns right of the name, each where it actually renders. */
interface Frozen {
  rate: Slot;
  hours: Slot;
  amount: Slot;
}

/**
 * The frozen columns' sticky `left` offsets, MEASURED from the rendered header row.
 *
 * The working grid sums its offsets from the slot widths, which holds only while every
 * frozen column renders exactly that wide. This table lays out automatically — it has to,
 * so a month column can grow to fit "$1,103,500" — and so a frozen column grows too when
 * a figure outgrows it: a 230,400-hour plan total widens Hours, a $1,250.50 rate widens
 * Rate. A summed offset would then pin the next column on top of the one before it as soon
 * as the grid scrolled sideways. Measured in a layout effect, so before the first paint,
 * and again whenever a frozen header cell changes size (the web font arriving, a refetch).
 */
function useFrozen(headRow: HTMLTableRowElement | null): Frozen {
  const [lefts, setLefts] = useState({
    rate: GEO.rate.left,
    hours: GEO.hours.left,
    amount: GEO.amount.left,
  });

  useLayoutEffect(() => {
    if (headRow === null) return;
    const [category, rate, hours] = Array.from(headRow.cells);
    if (category === undefined || rate === undefined || hours === undefined) return;
    const measure = () => {
      const toRate = category.getBoundingClientRect().width;
      const toHours = toRate + rate.getBoundingClientRect().width;
      const toAmount = toHours + hours.getBoundingClientRect().width;
      setLefts((prev) =>
        prev.rate === toRate && prev.hours === toHours && prev.amount === toAmount
          ? prev
          : { rate: toRate, hours: toHours, amount: toAmount },
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    for (const cell of [category, rate, hours]) observer.observe(cell);
    return () => observer.disconnect();
  }, [headRow]);

  return useMemo(
    () => ({
      rate: { left: lefts.rate, width: GEO.rate.width },
      hours: { left: lefts.hours, width: GEO.hours.width },
      amount: { left: lefts.amount, width: GEO.amount.width },
    }),
    [lefts],
  );
}

/** Blank for no hours, the way an empty cell reads on the working grid. */
function cellHours(value: string | undefined): string {
  return value === undefined || value === "0.00" ? "" : fmtNum(value);
}

/*
 * A Workstream band's tint, OPAQUE — `bg-muted/40` mixed onto the card rather than laid
 * over it. The band's frozen cell is sticky, and a translucent sticky cell lets the month
 * subtotals scroll visibly through it (the hazard PlanGrid's ramp row documents). The
 * month cells use the same mix so the band reads as one colour end to end.
 */
const BAND_DEFAULT = "bg-[color-mix(in_oklab,var(--muted)_20%,var(--card))]";
const BAND_WORKSTREAM = "bg-[color-mix(in_oklab,var(--muted)_40%,var(--card))]";

/*
 * The horizontal padding of a `px-3` cell. A name cell's content is pinned to its slot
 * width less this, so a long Labor Category or Workstream name truncates (full text on
 * hover) instead of widening the frozen block for every row.
 */
const PAD_X3 = 24;

export function SharedPlanGrid({ plan }: { plan: SharedPlanView }) {
  const groups = useMemo(() => sharedGridGroups(plan), [plan]);
  const byMonth = useMemo(() => new Map(plan.byMonth.map((m) => [m.month, m])), [plan.byMonth]);
  const [headRow, setHeadRow] = useState<HTMLTableRowElement | null>(null);
  const frozen = useFrozen(headRow);
  const { months } = plan;

  if (months.length === 0 || groups.length === 0) {
    return (
      <div className="rounded-md border border-border bg-card px-6 py-12 text-center text-sm text-muted-foreground">
        {months.length === 0 ? "This plan has no months yet." : "No Plan Lines yet."}
      </div>
    );
  }

  const workstreamCount = groups.filter((g) => g.workstream.id !== null).length;
  const lineCount = groups.reduce((sum, g) => sum + g.lines.length, 0);
  // The Total row sits above the Revenue and FTE rows — see FOOT_SUB_ROW_H.
  const totalRowBottom = FOOT_SUB_ROW_H * 2;

  return (
    <div className="flex flex-col gap-2">
      {/*
       * `w-fit`, unlike the working grid: a shared plan is often short enough to fit the
       * page, and a full-width card would end its header and footer bands short of its own
       * right edge. Capped at the page width, past which it scrolls like the working grid.
       */}
      <div
        className="relative w-fit max-w-full overflow-auto rounded-md border border-border bg-card"
        style={{ maxHeight: "min(68vh, 720px)" }}
      >
        <table
          className="border-collapse text-sm"
          style={{ width: GEO.frozenWidth + months.length * W_MONTH }}
        >
          <thead>
            <tr ref={setHeadRow}>
              <HeadCell slot={GEO.category} align="left">
                Labor Category
              </HeadCell>
              <HeadCell slot={frozen.rate} align="right">
                Rate
              </HeadCell>
              <HeadCell slot={frozen.hours} align="right">
                Hours
              </HeadCell>
              <HeadCell slot={frozen.amount} align="right" edge>
                Amount
              </HeadCell>
              {months.map((month) => (
                <th
                  key={month}
                  // nowrap: a month column that is only as wide as "$81,500" would otherwise
                  // break "MAR 27" over two lines.
                  className="sticky top-0 z-20 whitespace-nowrap border-l border-border bg-muted px-1.5 py-2 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground tabular-nums"
                  style={{ width: W_MONTH, minWidth: W_MONTH }}
                >
                  {monthLabel(month)}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {groups.map((group) => (
              <GroupRows
                key={group.workstream.id ?? "default"}
                group={group}
                months={months}
                frozen={frozen}
              />
            ))}
          </tbody>

          <tfoot>
            <tr>
              <FootCell
                left={0}
                width={GEO.hours.left}
                colSpan={GEO.frozenCols - 2}
                align="left"
                bottom={totalRowBottom}
              >
                Total ·{" "}
                {workstreamCount > 0
                  ? `${workstreamCount} Workstream${workstreamCount === 1 ? "" : "s"} · `
                  : ""}
                {lineCount} Plan Line{lineCount === 1 ? "" : "s"}
              </FootCell>
              <FootCell slot={frozen.hours} align="right" strong bottom={totalRowBottom}>
                {fmtNum(plan.totalHours)}
              </FootCell>
              <FootCell slot={frozen.amount} align="right" strong edge bottom={totalRowBottom}>
                {fmtMoney(plan.indicativeRevenue)}
              </FootCell>
              {months.map((month) => (
                <td
                  key={month}
                  className="sticky z-20 border-l border-t-2 border-border bg-muted px-1.5 py-2 text-right text-sm font-semibold tabular-nums"
                  style={{ width: W_MONTH, bottom: totalRowBottom }}
                >
                  {fmtNum(byMonth.get(month)?.totalHours ?? "0.00")}
                </td>
              ))}
            </tr>
            <tr>
              <FootCell
                left={0}
                width={GEO.frozenWidth}
                colSpan={GEO.frozenCols}
                align="left"
                muted
                edge
                bottom={FOOT_SUB_ROW_H}
                height={FOOT_SUB_ROW_H}
              >
                Revenue per month
                <span
                  className="ml-1 cursor-help text-muted-foreground/70"
                  title="Hours × bill rate for the month, across every Plan Line. Rounded to the dollar; the Amount column carries the exact figure."
                >
                  ⓘ
                </span>
              </FootCell>
              {months.map((month) => {
                const revenue = byMonth.get(month)?.totalRevenue ?? "0.00";
                return (
                  <td
                    key={month}
                    className="sticky z-20 border-l border-border bg-muted px-1.5 text-right text-xs font-semibold tabular-nums"
                    style={{ width: W_MONTH, bottom: FOOT_SUB_ROW_H, height: FOOT_SUB_ROW_H }}
                    title={fmtMoney(revenue)}
                  >
                    {fmtWholeMoney(revenue)}
                  </td>
                );
              })}
            </tr>
            <tr>
              <FootCell
                left={0}
                width={GEO.frozenWidth}
                colSpan={GEO.frozenCols}
                align="left"
                muted
                edge
                height={FOOT_SUB_ROW_H}
              >
                FTE per month
                <span
                  className="ml-1 cursor-help text-muted-foreground/70"
                  title="A reference figure: hours ÷ Hours per FTE-month."
                >
                  ⓘ
                </span>
              </FootCell>
              {months.map((month) => (
                <td
                  key={month}
                  className="sticky bottom-0 z-20 border-l border-border bg-muted px-1.5 text-right text-xs text-muted-foreground tabular-nums"
                  style={{ width: W_MONTH, height: FOOT_SUB_ROW_H }}
                >
                  {byMonth.get(month)?.fte ?? "0.00"}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>

      {plan.ramped === true && (
        <p className="px-1 text-xs text-muted-foreground">
          Hours include the plan&rsquo;s ramp-up: each month shows the hours it will bill.
        </p>
      )}
    </div>
  );
}

/** A Workstream's header band, then its lines — the working grid's GroupBlock, read-only. */
function GroupRows({
  group,
  months,
  frozen,
}: {
  group: SharedGridGroup;
  months: readonly string[];
  frozen: Frozen;
}) {
  const ws = group.workstream;
  // The default group's header is deliberately quiet, as on the working grid: it is a
  // bucket, not a category.
  const isDefault = ws.id === null;
  const band = isDefault ? BAND_DEFAULT : BAND_WORKSTREAM;
  const count = group.lines.length;

  return (
    <>
      <tr>
        <td
          colSpan={GEO.frozenCols}
          className={cn("sticky left-0 z-[15] border-y border-r-2 border-border px-3 py-1.5", band)}
          style={{ width: GEO.frozenWidth }}
        >
          <div className="flex items-center gap-2" style={{ width: GEO.frozenWidth - PAD_X3 }}>
            <span
              className={cn(
                "min-w-0 truncate",
                isDefault
                  ? "text-xs font-semibold uppercase tracking-wider text-muted-foreground"
                  : "text-sm font-semibold",
              )}
              title={isDefault ? "Plan Lines that are not grouped into a Workstream." : ws.name}
            >
              {ws.name}
            </span>
            {ws.type !== null && <WorkstreamTypeChip type={ws.type} />}
            <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
              {count} line{count === 1 ? "" : "s"} · {fmtNum(ws.totalHours)} hrs
            </span>
          </div>
        </td>
        {months.map((month) => (
          <td
            key={month}
            className={cn(
              "border-y border-l border-border px-1.5 py-1.5 text-right text-xs text-muted-foreground tabular-nums",
              band,
            )}
            style={{ width: W_MONTH }}
          >
            {cellHours(group.hoursByMonth.get(month))}
          </td>
        ))}
      </tr>

      {group.lines.map((row) => (
        <LineRow key={row.line.id} row={row} months={months} frozen={frozen} />
      ))}
    </>
  );
}

/** One Plan Line — the working grid's GridRow, with text where it has inputs. */
function LineRow({
  row,
  months,
  frozen,
}: {
  row: SharedGridLine;
  months: readonly string[];
  frozen: Frozen;
}) {
  const { line } = row;
  const name = line.clientLaborCategory ?? line.laborCategoryName;
  return (
    <tr>
      <td
        className="sticky left-0 z-10 border-b border-border bg-card px-3"
        style={{ width: GEO.category.width, height: 34 }}
      >
        {/*
         * The Client Label stands in for the Labor Category, as on the working grid: on a
         * shared page it is the name the client recognises. The Labor Category behind it
         * stays one hover away.
         */}
        <span className="flex items-center gap-1.5" style={{ width: GEO.category.width - PAD_X3 }}>
          <span
            className="flex min-w-0 flex-1 items-center gap-1.5"
            title={
              line.clientLaborCategory === null
                ? name
                : `${name} — client label for ${line.laborCategoryName}`
            }
          >
            <span className="truncate text-sm font-medium">{name}</span>
            {line.disciplineCode !== null && (
              <span
                className="shrink-0 text-xs font-semibold text-muted-foreground"
                title={`Maps to the ${line.disciplineCode} Discipline`}
              >
                {line.disciplineCode}
              </span>
            )}
          </span>
          <LineStatusBadge status={line.lineStatus} />
        </span>
      </td>

      <td
        className="sticky z-10 border-b border-border bg-card px-2 text-right text-sm tabular-nums"
        style={{ left: frozen.rate.left, width: frozen.rate.width }}
      >
        {fmtMoney(line.billRate)}
      </td>

      <td
        className="sticky z-10 border-b border-border bg-card px-2 text-right text-sm font-medium tabular-nums"
        style={{ left: frozen.hours.left, width: frozen.hours.width }}
      >
        {fmtNum(line.totalHours)}
      </td>

      <td
        className="sticky z-10 border-b border-r-2 border-border bg-card px-2.5 text-right text-sm text-muted-foreground tabular-nums"
        style={{ left: frozen.amount.left, width: frozen.amount.width }}
      >
        {fmtMoney(line.totalRevenue)}
      </td>

      {months.map((month) => (
        <td
          key={month}
          className="border-b border-l border-border px-1.5 text-right text-sm tabular-nums"
          style={{ width: W_MONTH }}
        >
          {cellHours(row.hoursByMonth.get(month))}
        </td>
      ))}
    </tr>
  );
}
